import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Dialog, Text, Flex, Spacer } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import type { ReleaseNote } from 'store/entities/release-notes/release-notes.types';

interface LatestUpdatesModalProps {
  onClose: () => void;
  releaseNotes: ReleaseNote[];
}

const DATE_LOCALE = 'en-GB';
const DIALOG_WIDTH = '600px';

/**
 * Placeholders inside the translation strings that get swapped for the
 * (possibly pluralised) runtime values.
 */
const PLACEHOLDER = {
  count: '#',
  release: 'release',
  link: 'link',
} as const;

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString(DATE_LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const byPublishedAtDesc = (first: ReleaseNote, second: ReleaseNote): number =>
  new Date(second.publishedAt).getTime() - new Date(first.publishedAt).getTime();

const openInNewTab = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const LatestUpdatesModal = ({ onClose, releaseNotes }: LatestUpdatesModalProps) => {
  const { t } = useTranslation();

  const sortedNotes = [...releaseNotes].sort(byPublishedAtDesc);

  const releaseCount = sortedNotes.length;
  const hasMultipleReleases = releaseCount !== 1;

  const releaseWord = hasMultipleReleases ? t(translations.navigation_releases) : t(translations.navigation_release);

  const linkWord = hasMultipleReleases ? t(translations.navigation_links) : t(translations.navigation_link);

  const countText = t(translations.navigation_latestUpdatesCountText)
    .replace(PLACEHOLDER.count, `${releaseCount}`)
    .replace(PLACEHOLDER.release, releaseWord);

  const summaryAfter = t(translations.navigation_latestUpdatesSummaryAfter).replace(PLACEHOLDER.link, linkWord);

  return (
    <Modal visible centered>
      <Dialog
        dialog={{
          width: DIALOG_WIDTH,
          heading: t(translations.navigation_latestUpdates),
          content: (
            <>
              <Text>
                {t(translations.navigation_latestUpdatesSummaryBefore)} <Text bold>{countText}</Text> {summaryAfter}
              </Text>
              <Flex direction="column">
                {sortedNotes.map((note) => (
                  <React.Fragment key={note.id}>
                    <Spacer />
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text bold>{formatDate(note.publishedAt)}</Text>
                      <Text link onClick={() => openInNewTab(note.externalUrl)}>
                        {t(translations.navigation_latestUpdatesMoreInfo)}
                      </Text>
                    </Flex>
                  </React.Fragment>
                ))}
              </Flex>
            </>
          ),
          footer: (
            <Button label={t(translations.navigation_latestUpdatesCloseReload)} colored="orange" onClick={onClose} />
          ),
        }}
      />
    </Modal>
  );
};
